import React, { useEffect, useState, useCallback } from 'react'
import { Table, Button, Select, InputNumber, Input, Modal, Form, Space, message, Typography, Popconfirm } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { warehouseApi, productApi } from '../../services/api'
import dayjs from 'dayjs'

const { Title } = Typography

export default function StockIn() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 20

  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm()

  const [products, setProducts] = useState([])
  const [productSearch, setProductSearch] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await warehouseApi.stockIns({ page, pageSize })
      setData(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    productApi.list({ pageSize: 200 }).then((res) => setProducts(res.data)).catch(() => {})
  }, [])

  const handleSubmit = async (values) => {
    try {
      await warehouseApi.createStockIn(values)
      message.success('入库成功')
      setModalOpen(false)
      form.resetFields()
      fetchData()
    } catch (e) {
      message.error(e?.error || '入库失败')
    }
  }

  const handleDelete = async (id) => {
    try {
      await warehouseApi.deleteStockIn(id)
      message.success('已删除')
      fetchData()
    } catch (e) {
      message.error(e?.error || '删除失败')
    }
  }

  const columns = [
    {
      title: '产品', key: 'product', width: 180,
      render: (_, r) => r.product?.name || '—',
    },
    {
      title: 'SKU', key: 'sku', width: 100,
      render: (_, r) => r.product?.sku || '—',
    },
    {
      title: '数量', dataIndex: 'quantity', key: 'quantity', width: 80,
    },
    {
      title: '单价', dataIndex: 'unitPrice', key: 'unitPrice', width: 100,
      render: (v) => v ? `¥${Number(v).toLocaleString()}` : '—',
    },
    {
      title: '采购单号', dataIndex: 'purchaseNo', key: 'purchaseNo', width: 120,
      render: (v) => v || '—',
    },
    {
      title: '入库日期', dataIndex: 'createdAt', key: 'createdAt', width: 140,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作人', key: 'operator', width: 80,
      render: (_, r) => r.operator?.name || '—',
    },
    {
      title: '操作', key: 'actions', width: 80,
      render: (_, r) => (
        <Popconfirm title="确认删除该入库记录？" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消">
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      ),
    },
  ]

  const filteredProducts = productSearch
    ? products.filter((p) =>
        p.name.includes(productSearch) || p.sku.includes(productSearch)
      )
    : products

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>入库管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true) }}>新入库</Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          total,
          current: page,
          pageSize,
          showTotal: (t) => `共 ${t} 条入库记录`,
          onChange: (p) => setPage(p),
        }}
      />

      <Modal
        title="新增入库"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="确认入库"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="productId" label="产品" rules={[{ required: true, message: '请选择产品' }]}>
            <Select
              showSearch
              placeholder="搜索/选择产品"
              filterOption={false}
              onSearch={(v) => setProductSearch(v)}
              onChange={() => setProductSearch('')}
            >
              {filteredProducts.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name} ({p.sku})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="quantity" label="入库数量" rules={[{ required: true, message: '请输入数量' }]}>
            <InputNumber style={{ width: '100%' }} min={1} placeholder="入库数量" />
          </Form.Item>
          <Form.Item name="unitPrice" label="单价 (¥)">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="采购单价（可选）" />
          </Form.Item>
          <Form.Item name="purchaseNo" label="采购单号">
            <Input placeholder="采购单号（可选）" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="入库备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
